/**
 * Komal OS - CRM: the pipeline (deals by stage).
 *
 * Owner-scoped server-side (see app/routes/crm_routes.py's _owner_scope) -
 * a non-CEO Sales Department user only ever gets their own deals back.
 * Moving a card's stage is a plain <select> (not drag-and-drop) - simpler
 * and more reliable to ship in this stage; worth revisiting once the
 * board is in daily use.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Search } from 'lucide-react';
import {
  listDeals, updateDeal, createDeal, searchAccounts, listEvents, listProducts, createProduct, listTeam,
} from '../../services/crmService';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';

const STAGES = [
  { key: 'new', label: 'New', bar: 'bg-blue-400' },
  { key: 'contacted', label: 'Contacted', bar: 'bg-blue-400' },
  { key: 'qualified', label: 'Qualified', bar: 'bg-purple-400' },
  { key: 'quoted', label: 'Quoted', bar: 'bg-komal-blue' },
  { key: 'won', label: 'Won', bar: 'bg-green-500' },
  { key: 'lost', label: 'Lost', bar: 'bg-red-400' },
];

const emptyForm = () => ({
  account_id: null, account_name: '', title: '', value: '', currency: 'INR',
  expected_close_date: '', event_id: '', product_ids: [],
});

export default function PipelinePage() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerId, setOwnerId] = useState('');
  const [team, setTeam] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [accountQuery, setAccountQuery] = useState('');
  const [accountResults, setAccountResults] = useState([]);
  const [newProductName, setNewProductName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listDeals({ owner_id: ownerId || undefined })
      .then((res) => setDeals(res.data.deals))
      .catch((err) => showToast(err.response?.data?.detail || 'Could not load the pipeline', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listTeam().then((res) => setTeam(res.data.team)).catch(() => {});
    listEvents().then((res) => setEvents(res.data.events)).catch(() => {});
    listProducts().then((res) => setProducts(res.data.products)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [ownerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accountQuery.trim()) { setAccountResults([]); return undefined; }
    const timer = setTimeout(() => {
      searchAccounts(accountQuery).then((res) => setAccountResults(res.data.accounts)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [accountQuery]);

  const moveStage = async (deal, stage) => {
    // Optimistic - the board should feel instant; reverts on failure.
    setDeals((prev) => prev.map((d) => (d.id === deal.id ? { ...d, stage } : d)));
    try {
      await updateDeal(deal.id, {
        account_id: deal.account.id, title: deal.title, value: deal.value, currency: deal.currency,
        stage, expected_close_date: deal.expected_close_date, event_id: deal.event?.id || null,
        won_lost_reason: deal.won_lost_reason, product_ids: deal.products.map((p) => p.id),
      });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not move the deal', 'error');
      load();
    }
  };

  const toggleProduct = (id) => {
    setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  };

  const addNewProduct = async () => {
    if (!newProductName.trim()) return;
    try {
      const res = await createProduct({ name: newProductName.trim() });
      setProducts((prev) => (prev.some((p) => p.id === res.data.product.id) ? prev : [...prev, res.data.product]));
      setForm((f) => ({ ...f, product_ids: [...f.product_ids, res.data.product.id] }));
      setNewProductName('');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add product', 'error');
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.account_id) { showToast('Pick a customer first', 'error'); return; }
    if (!form.title.trim()) { showToast('Deal title is required', 'error'); return; }
    setSaving(true);
    try {
      await createDeal({
        account_id: form.account_id, title: form.title,
        value: form.value ? Number(form.value) : null, currency: form.currency,
        expected_close_date: form.expected_close_date || null,
        event_id: form.event_id || null, product_ids: form.product_ids,
      });
      showToast('Deal created', 'success');
      setForm(emptyForm());
      setAccountQuery('');
      setCreating(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not create the deal', 'error');
    } finally {
      setSaving(false);
    }
  };

  const byStage = (stage) => deals.filter((d) => d.stage === stage);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-full">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-white">Pipeline</h1>
              <p className="text-sm text-white/70 mt-0.5">{user?.role === 'CEO' ? 'Every open deal across the team.' : 'Deals you own.'}</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="text-sm rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2">
                <option value="" className="text-gray-800">Owner: All</option>
                {team.map((t) => <option key={t.id} value={t.id} className="text-gray-800">{t.name}</option>)}
              </select>
              <button onClick={() => setCreating((v) => !v)} className="flex items-center gap-1.5 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3.5 py-2">
                <Plus size={15} /> New deal
              </button>
            </div>
          </div>

          {creating && (
            <form onSubmit={submitCreate} className="glass-card p-5 mb-5 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">New deal</span>
                <button type="button" onClick={() => setCreating(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>

              <div className="sm:col-span-2 relative">
                {form.account_id ? (
                  <div className="flex items-center justify-between border border-gray-300 rounded px-3 py-2 text-sm bg-gray-50">
                    <span className="font-medium">{form.account_name}</span>
                    <button type="button" onClick={() => setForm({ ...form, account_id: null, account_name: '' })} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input placeholder="Search customer…" value={accountQuery} onChange={(e) => setAccountQuery(e.target.value)} className="w-full pl-8 border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    {accountResults.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {accountResults.map((a) => (
                          <button type="button" key={a.id} onClick={() => { setForm({ ...form, account_id: a.id, account_name: a.name }); setAccountResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                            {a.name} {a.country && <span className="text-gray-400">· {a.country}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <input required placeholder="Deal title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sm:col-span-2 border border-gray-300 rounded px-3 py-2 text-sm" />
              <input type="number" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                {['INR', 'USD', 'EUR', 'AED', 'GBP'].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
              <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">No event</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>

              <div className="sm:col-span-2">
                <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Products quoted</div>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {products.map((p) => (
                    <button type="button" key={p.id} onClick={() => toggleProduct(p.id)}
                      className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${form.product_ids.includes(p.id) ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white text-gray-600 border-gray-300'}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input placeholder="Add a new product…" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                  <button type="button" onClick={addNewProduct} className="text-xs font-semibold text-komal-blue border border-gray-300 rounded px-2">Add</button>
                </div>
              </div>

              <button disabled={saving} className="sm:col-span-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60">
                {saving ? 'Saving…' : 'Create deal'}
              </button>
            </form>
          )}
        </div>

        {loading ? (
          <p className="text-white/70 text-sm px-1">Loading…</p>
        ) : (
          <div className="grid gap-3 overflow-x-auto pb-2" style={{ gridTemplateColumns: 'repeat(6, minmax(210px, 1fr))' }}>
            {STAGES.map((stage) => {
              const stageDeals = byStage(stage.key);
              return (
                <div key={stage.key}>
                  <div className="flex items-center justify-between px-1 mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-white/80">{stage.label}</span>
                    <span className="text-[11px] text-white/60 bg-white/10 border border-white/20 rounded-full px-2">{stageDeals.length}</span>
                  </div>
                  <div className={`h-[3px] rounded-full mb-2.5 ${stage.bar}`} />
                  <div className="space-y-2.5">
                    {stageDeals.map((d) => (
                      <div key={d.id} className="glass-card p-3">
                        <Link to={`/crm/accounts/${d.account?.id}`} className="text-sm font-bold text-gray-800 hover:underline block truncate">{d.account?.name}</Link>
                        <div className="text-xs text-gray-500 truncate">{d.title}</div>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {d.products.map((p) => <span key={p.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{p.name}</span>)}
                        </div>
                        {d.value != null && <div className="text-sm font-bold text-gray-800 mt-2">{d.currency} {Number(d.value).toLocaleString()}</div>}
                        <div className="flex items-center justify-between mt-2">
                          <select
                            value={d.stage}
                            onChange={(e) => moveStage(d, e.target.value)}
                            className="text-[11px] border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600"
                          >
                            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                          </select>
                          {d.expected_close_date && <span className="text-[11px] text-gray-400">{d.expected_close_date}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
